import React, { useState } from 'react';
import {
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../../lib/firebase';
import QRCode from 'qrcode';

interface MFASetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

const MFASetup: React.FC<MFASetupProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<'generate' | 'verify' | 'reauth' | 'verify-email'>('generate');
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleSendVerificationEmail = async () => {
    try {
      setLoading(true);
      setError('');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }
      await sendEmailVerification(user);
      setEmailSent(true);
    } catch (err: any) {
      setError('Hiba a hitelesítő email küldése során: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    try {
      setLoading(true);
      setError('');
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }
      await user.reload();
      if (user.emailVerified) {
        setStep('generate');
        await generateQRCode();
      } else {
        setError('Az email cím még nincs visszaigazolva. Kattints a linkre az emailben.');
      }
    } catch (err: any) {
      setError('Hiba az ellenőrzés során: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReauthenticate = async () => {
    try {
      setLoading(true);
      setError('');

      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }

      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      
      setPassword('');
      setStep('generate');
      
      // Automatikusan próbáljuk újra a QR kód generálást
      await generateQRCode();
    } catch (err: any) {
      console.error('Error reauthenticating:', err);
      if (err.code === 'auth/wrong-password') {
        setError('Hibás jelszó');
      } else {
        setError('Hiba az újra-hitelesítés során: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    try {
      setLoading(true);
      setError('');

      const user = auth.currentUser;
      if (!user) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }

      if (!user.emailVerified) {
        setStep('verify-email');
        return;
      }

      const multiFactorSession = await multiFactor(user).getSession();
      
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
      
      const otpAuthUrl = totpSecret.generateQrCodeUrl(
        user.email || 'EnergiaInfo CRM',
        'EnergiaInfo CRM'
      );

      // Generate QR code as base64 image
      const qrCodeDataUrl = await QRCode.toDataURL(otpAuthUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setTotpSecret(totpSecret);
      setQrCodeUrl(qrCodeDataUrl);
      setStep('verify');
    } catch (err: any) {
      console.error('Error generating QR code:', err);
      if (err.code === 'auth/requires-recent-login') {
        setError('Biztonsági okokból újra be kell jelentkezned');
        setStep('reauth');
      } else {
        setError('Hiba a QR kód generálása során: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnableMFA = async () => {
    try {
      setLoading(true);
      setError('');

      if (!totpSecret) {
        throw new Error('Nincs TOTP secret');
      }

      const user = auth.currentUser;
      if (!user) {
        throw new Error('Nincs bejelentkezett felhasználó');
      }

      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode
      );

      await multiFactor(user).enroll(
        multiFactorAssertion,
        'Authenticator App'
      );

      onComplete();
    } catch (err: any) {
      console.error('Error verifying MFA:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Érvénytelen ellenőrző kód');
      } else {
        setError('Hiba az ellenőrzés során: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Kétfaktoros hitelesítés beállítása
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {step === 'reauth' && (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-semibold text-orange-900 mb-2">
                Újra-hitelesítés szükséges
              </h3>
              <p className="text-sm text-orange-800">
                Biztonsági okokból add meg a jelszavadat a kétfaktoros hitelesítés beállításához.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jelszó
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && password && handleReauthenticate()}
                placeholder="Add meg a jelszavadat"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleReauthenticate}
                disabled={loading || !password}
                className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 transition-all"
              >
                {loading ? 'Ellenőrzés...' : 'Folytatás'}
              </button>
              <button
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Mégse
              </button>
            </div>
          </div>
        )}

        {step === 'verify-email' && (
          <div className="space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-semibold text-orange-900 mb-2">
                Email visszaigazolás szükséges
              </h3>
              <p className="text-sm text-orange-800">
                A kétfaktoros hitelesítés bekapcsolásához először vissza kell igazolnod az email címedet.
              </p>
            </div>

            {!emailSent ? (
              <button
                onClick={handleSendVerificationEmail}
                disabled={loading}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 transition-all"
              >
                {loading ? 'Küldés...' : 'Hitelesítő email küldése'}
              </button>
            ) : (
              <>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    Hitelesítő email elküldve! Ellenőrizd a postaládádat és kattints a linkre az emailben.
                  </p>
                </div>
                <button
                  onClick={handleCheckVerification}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 transition-all"
                >
                  {loading ? 'Ellenőrzés...' : 'Visszaigazoltam, folytatás'}
                </button>
              </>
            )}

            <button
              onClick={onCancel}
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Mégse
            </button>
          </div>
        )}

        {step === 'generate' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">
                Mire lesz szükséged:
              </h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Google Authenticator alkalmazás</li>
                <li>Microsoft Authenticator alkalmazás</li>
                <li>Vagy bármely TOTP kompatibilis app</li>
              </ul>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                A kétfaktoros hitelesítés extra védelmet nyújt a fiókodnak. 
                Bejelentkezéskor a jelszó mellett egy 6 számjegyű kódot is meg kell adnod.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={generateQRCode}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 transition-all"
              >
                {loading ? 'Generálás...' : 'QR kód generálása'}
              </button>
              <button
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Mégse
              </button>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
              <p className="text-sm text-gray-600 mb-3 text-center">
                Szkenneld be ezt a QR kódot az Authenticator alkalmazásoddal:
              </p>
              {qrCodeUrl && (
                <div className="flex justify-center">
                  <img 
                    src={qrCodeUrl} 
                    alt="QR Code" 
                    className="w-48 h-48 border-4 border-white rounded-lg shadow-lg"
                  />
                </div>
              )}
            </div>

            {totpSecret && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800 font-semibold mb-1">
                  Manuális beállítási kulcs:
                </p>
                <code className="text-xs text-yellow-900 break-all bg-yellow-100 px-2 py-1 rounded">
                  {totpSecret.secretKey}
                </code>
                <p className="text-xs text-yellow-700 mt-2">
                  Mentsd el biztonságos helyre, ha elveszítenéd a hozzáférést!
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add meg a 6 számjegyű kódot az alkalmazásból:
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && verificationCode.length === 6) {
                    verifyAndEnableMFA();
                  }
                }}
                placeholder="000000"
                maxLength={6}
                autoFocus
                className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={verifyAndEnableMFA}
                disabled={loading || verificationCode.length !== 6}
                className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 rounded-lg hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 transition-all"
              >
                {loading ? 'Ellenőrzés...' : 'Bekapcsolás'}
              </button>
              <button
                onClick={() => setStep('generate')}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Vissza
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MFASetup;
